import { ReactNode, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Info, Receipt, Split, X } from 'lucide-react';

import CompactSelectField from '../../../../components/Forms/Input/CompactSelectField';
import ActionButton from '../../../../components/Buttons/ActionButton';

import {
  RestaurantOrderCheckDTO,
  RestaurantOrderDTO,
  RestaurantOrderItemDTO,
} from '../../../../types/restaurant/RestaurantOrderTypes';
import { RestaurantSplitCheckInput } from '../../../../services/restaurant/RestaurantOrderService';
import { formatOrderMoney, orderItemName } from '../orderPresentation';

interface Props {
  open: boolean;
  order: RestaurantOrderDTO | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSplit: (checks: RestaurantSplitCheckInput[]) => Promise<unknown> | void;
  onClear: () => Promise<unknown> | void;
  onBillCheck: (checkId: number) => void;
}

interface SplitUnit {
  key: string;
  label: string;
  detail: string;
  itemIds: number[];
  total: number;
}

const buildUnits = (items: RestaurantOrderItemDTO[]): SplitUnit[] => {
  const active = items.filter((item) => item.status === 'ACTIVE');
  const seenCombos = new Set<string>();
  const units: SplitUnit[] = [];

  active.forEach((item) => {
    const comboKey =
      item.comboInstanceId != null ? String(item.comboInstanceId) : null;
    if (comboKey) {
      if (seenCombos.has(comboKey)) return;
      seenCombos.add(comboKey);
      const members = active.filter(
        (candidate) => String(candidate.comboInstanceId) === comboKey
      );
      units.push({
        key: `combo-${comboKey}`,
        label: members[0]?.comboName || 'Combo',
        detail: `${members.length} ítems`,
        itemIds: members.map((member) => member.id),
        total: members.reduce(
          (sum, member) => sum + Number(member.subtotal ?? member.total ?? 0),
          0
        ),
      });
      return;
    }
    units.push({
      key: `item-${item.id}`,
      label: orderItemName(item),
      detail: `x${item.quantity}`,
      itemIds: [item.id],
      total: Number(item.subtotal ?? item.total ?? 0),
    });
  });

  return units;
};

const CHECK_TONE: Record<string, string> = {
  SETTLED: 'border-success/40 bg-success/5',
  BILLING: 'border-warning/40 bg-warning/5',
  OPEN: 'border-stroke dark:border-strokedark',
};

const statusMeta = (status: string) => {
  if (status === 'SETTLED')
    return { label: 'Pagada', chip: 'bg-success/10 text-success' };
  if (status === 'BILLING')
    return { label: 'En cobro', chip: 'bg-warning/10 text-warning' };
  return { label: 'Abierta', chip: 'bg-primary/10 text-primary' };
};

const OrderSplitBillModal = ({
  open,
  order,
  busy,
  onOpenChange,
  onSplit,
  onClear,
  onBillCheck,
}: Props) => {
  const units = useMemo(() => buildUnits(order?.items ?? []), [order?.items]);
  const checks = order?.checks ?? [];
  const hasSplit = checks.length > 0;
  const maxChecks = Math.max(2, Math.min(units.length, 6));

  const [checkCount, setCheckCount] = useState(2);
  const [assignment, setAssignment] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!open || hasSplit) return;
    const initial: Record<string, number> = {};
    units.forEach((unit, index) => {
      initial[unit.key] = index % 2;
    });
    setCheckCount(2);
    setAssignment(initial);
  }, [hasSplit, open, units]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onOpenChange, open]);

  if (!open || !order) return null;

  const buckets: SplitUnit[][] = Array.from({ length: checkCount }, () => []);
  units.forEach((unit) => {
    const index = Math.min(assignment[unit.key] ?? 0, checkCount - 1);
    buckets[index].push(unit);
  });
  const bucketTotal = (index: number) =>
    buckets[index]?.reduce((sum, unit) => sum + unit.total, 0) ?? 0;
  const everyBucketHasItems = buckets.every((bucket) => bucket.length > 0);

  const canBill = order.status === 'READY';
  const canClear = hasSplit && checks.every((check) => check.status === 'OPEN');
  const settledCount = checks.filter(
    (check) => check.status === 'SETTLED'
  ).length;

  const submitSplit = async () => {
    if (!everyBucketHasItems) return;
    const payload: RestaurantSplitCheckInput[] = buckets.map(
      (bucket, index) => ({
        label: `Cuenta ${index + 1}`,
        itemIds: bucket.flatMap((unit) => unit.itemIds),
      })
    );
    await onSplit(payload);
  };

  const renderCheckCard = (
    key: string | number,
    title: string,
    tone: string,
    total: number,
    rows: { id: string | number; label: string; detail: string; total: number }[],
    footer?: ReactNode,
    chip?: { label: string; chip: string }
  ) => (
    <article
      key={key}
      className={`flex flex-col rounded-xl border p-3 ${tone}`}
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 font-semibold text-black dark:text-white">
          {title}
          {chip && (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${chip.chip}`}
            >
              {chip.label}
            </span>
          )}
        </span>
        <strong className="text-black dark:text-white">
          {formatOrderMoney(total)}
        </strong>
      </div>

      <ul className="mt-2 flex-1 space-y-1 text-xs text-bodydark2">
        {rows.map((row) => (
          <li key={row.id} className="flex justify-between gap-2">
            <span className="truncate">
              {row.label}
              <span className="ml-1 opacity-70">{row.detail}</span>
            </span>
            <span className="shrink-0">{formatOrderMoney(row.total)}</span>
          </li>
        ))}
        {!rows.length && (
          <li className="rounded-lg border border-dashed border-stroke py-3 text-center dark:border-strokedark">
            Sin productos asignados
          </li>
        )}
      </ul>

      {footer && <div className="mt-3">{footer}</div>}
    </article>
  );

  return (
    <div
      className="fixed inset-0 z-[8999] flex items-center justify-center bg-black/60 px-3 py-6 backdrop-blur-[2px]"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-split-title"
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark"
      >
        <header className="flex items-center justify-between border-b border-stroke px-5 py-4 dark:border-strokedark">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-primary/10 p-2 text-primary">
              <Split className="h-5 w-5" />
            </span>
            <div>
              <h2
                id="order-split-title"
                className="font-semibold text-black dark:text-white"
              >
                Dividir cuenta
              </h2>
              <p className="mt-0.5 text-xs text-bodydark2">
                {hasSplit
                  ? `${settledCount} de ${checks.length} subcuentas pagadas.`
                  : 'Separá el consumo de la mesa en varias cuentas.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-2 text-bodydark2 transition hover:bg-gray-2 dark:hover:bg-meta-4"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="flex gap-2 rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs text-body dark:bg-primary/10 dark:text-bodydark">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <p>
              Cada subcuenta se cobra por separado en caja y genera su propia
              factura. La orden se cierra cuando todas quedan pagadas.
            </p>
          </div>

          {hasSplit ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {checks.map((check: RestaurantOrderCheckDTO) => {
                const meta = statusMeta(check.status);
                const rows = order.items
                  .filter((item) => check.itemIds.includes(item.id))
                  .map((item) => ({
                    id: item.id,
                    label: orderItemName(item),
                    detail: `x${item.quantity}`,
                    total: Number(item.subtotal ?? item.total ?? 0),
                  }));
                return renderCheckCard(
                  check.id,
                  check.label,
                  CHECK_TONE[check.status] ?? CHECK_TONE.OPEN,
                  Number(check.total ?? 0),
                  rows,
                  check.status === 'OPEN' ? (
                    <ActionButton
                      label="Cobrar subcuenta"
                      icon={<Receipt className="h-3.5 w-3.5" />}
                      variant="primary"
                      size="sm"
                      fullWidth
                      disabled={busy || !canBill}
                      title={
                        canBill
                          ? undefined
                          : 'La orden debe estar lista para cobrar'
                      }
                      onClick={() => onBillCheck(check.id)}
                    />
                  ) : (
                    <p className="flex items-center gap-1.5 text-xs font-medium text-bodydark2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                      {check.status === 'SETTLED'
                        ? 'Cobrada'
                        : 'Enviada a caja'}
                    </p>
                  ),
                  meta
                );
              })}
            </div>
          ) : (
            <>
              <CompactSelectField
                label="¿En cuántas cuentas se divide?"
                name="splitCheckCount"
                value={checkCount}
                options={Array.from(
                  { length: maxChecks - 1 },
                  (_, index) => index + 2
                ).map((value) => ({
                  value,
                  label: `${value} cuentas`,
                }))}
                onChange={(event) => setCheckCount(Number(event.target.value))}
              />

              <div className="grid gap-3 sm:grid-cols-2">
                {buckets.map((bucket, index) =>
                  renderCheckCard(
                    index,
                    `Cuenta ${index + 1}`,
                    bucket.length
                      ? 'border-primary/30 bg-primary/5'
                      : 'border-danger/40 bg-danger/5',
                    bucketTotal(index),
                    bucket.map((unit) => ({
                      id: unit.key,
                      label: unit.label,
                      detail: unit.detail,
                      total: unit.total,
                    }))
                  )
                )}
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-black dark:text-white">
                  Asigná cada producto a una cuenta
                </p>
                <div className="space-y-1.5">
                  {units.map((unit) => (
                    <div
                      key={unit.key}
                      className="flex items-center justify-between gap-3 rounded-lg border border-stroke px-3 py-2 dark:border-strokedark"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {unit.label}
                        <span className="ml-1.5 text-xs text-bodydark2">
                          {unit.detail} · {formatOrderMoney(unit.total)}
                        </span>
                      </span>
                      <div className="w-28 shrink-0">
                        <CompactSelectField
                          name={`assign-${unit.key}`}
                          value={Math.min(
                            assignment[unit.key] ?? 0,
                            checkCount - 1
                          )}
                          options={Array.from(
                            { length: checkCount },
                            (_, index) => index
                          ).map((index) => ({
                            value: index,
                            label: `Cuenta ${index + 1}`,
                          }))}
                          onChange={(event) =>
                            setAssignment((current) => ({
                              ...current,
                              [unit.key]: Number(event.target.value),
                            }))
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {!everyBucketHasItems && (
                  <p className="mt-2 text-xs text-danger">
                    Cada cuenta necesita al menos un producto.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <footer className="flex items-center justify-end gap-3 border-t border-stroke p-4 dark:border-strokedark">
          <ActionButton
            label="Cerrar"
            variant="outline"
            size="sm"
            fullWidth={false}
            onClick={() => onOpenChange(false)}
          />
          {hasSplit ? (
            <ActionButton
              label="Quitar división"
              variant="danger"
              size="sm"
              fullWidth={false}
              loading={busy}
              disabled={busy || !canClear}
              title={
                canClear
                  ? undefined
                  : 'No se puede: una subcuenta ya está en cobro o pagada'
              }
              onClick={() => onClear()}
            />
          ) : (
            <ActionButton
              label="Dividir cuenta"
              icon={<Split className="h-4 w-4" />}
              variant="primary"
              size="sm"
              fullWidth={false}
              loading={busy}
              disabled={busy || !everyBucketHasItems || units.length < 2}
              onClick={submitSplit}
            />
          )}
        </footer>
      </section>
    </div>
  );
};

export default OrderSplitBillModal;
